import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Box, Button, Divider, Link, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { useToken } from "@rentablo/api";
import WarningIcon from "@icons/myicons/warning.svg?react";
import InfoIcon from "@icons/myicons/info.svg?react";
import { SettingsLayout } from "@features/settings/components/SettingsLayout";
import { ConfirmDeleteDialog } from "@components";
import { useBankConnections } from "@features/settings/SettingsBankConnections/useBankConnections";
import {
  useDeleteBankingApiConnectionV2,
  useDeleteUser,
  useUpdateUserProfile,
  useUserProfile,
} from "@rentablo/api/apiv1";
import { MandatorConditional } from "@helpers";

type Props = {};

export const SettingsAdvancedPage = (props: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = useToken({ enabled: false });
  const navigate = useNavigate();
  const [
    isConfirmDeleteBankingApiConnectionModalOpen,
    setIsConfirmDeleteBankingApiConnectionModalOpen,
  ] = useState(false);
  const [isConfirmDeleteAccountModalOpen, setIsConfirmDeleteAccountModalOpen] =
    useState(false);
  const [confirmDeleteAccountError, setConfirmDeleteAccountError] =
    useState<string>();
  const [
    confirmDeleteBankingApiConnectionError,
    setConfirmDeleteBankingApiConnectionError,
  ] = useState<string>();
  const { data: userProfile } = useUserProfile();
  const { mutate: deleteUser, isPending: isUserDeleting } = useDeleteUser();
  const { mutate: disableTracking, isPending: isTrackingDisabling } =
    useUpdateUserProfile();
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(true);
  const {
    mutate: deleteBankingApiConnection,
    isPending: isBankingApiConnectionDeleting,
  } = useDeleteBankingApiConnectionV2();

  const { finApiBankConnections, wealthApiBankConnections } =
    useBankConnections();
  const hasBankConnections = Boolean(
    [...finApiBankConnections, ...wealthApiBankConnections].length
  );

  const handleUserDelete = () => {
    deleteUser(undefined, {
      onSuccess: () => {
        setIsConfirmDeleteAccountModalOpen(false);
        enqueueSnackbar(
          t(
            "settings.advanced.deleteAccount.deleteAccountConfirmModal.success"
          ),
          {
            variant: "success",
            autoHideDuration: 3000,
          }
        );
        setTimeout(() => {
          logout();
          navigate("/signup");
        }, 3000);
      },
      onError: (err: AxiosApiError) => {
        setConfirmDeleteAccountError(err.response?.data.message);
      },
    });
  };

  const handleTrackingDisabling = () => {
    disableTracking(
      {
        id: Number(userProfile?.id),
        createOrUpdateProfileParams: {
          isTrackingEnabled: !isTrackingEnabled,
        },
      },
      {
        onSuccess: () => {
          isTrackingEnabled
            ? setIsTrackingEnabled(false)
            : setIsTrackingEnabled(true);
        },
      }
    );
  };

  const handleBankingApiConnectionDelete = () => {
    deleteBankingApiConnection(undefined, {
      onError: (err: AxiosApiError) => {
        setConfirmDeleteBankingApiConnectionError(err.response?.data.message);
      },
    });
  };

  useEffect(() => {
    if (!userProfile?.isTrackingEnabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from prop
      setIsTrackingEnabled(false);
      return;
    }
    setIsTrackingEnabled(true);
  }, [userProfile?.isTrackingEnabled]);

  return (
    <SettingsLayout
      breadcrumbs={[
        { text: t("settings.sidebar.title") },
        { text: t("settings.sidebar.common.advanced") },
      ]}
      title={t("settings.advanced.title")}
      subtitle={t("settings.advanced.subtitle")}
    >
      <Box>
        {hasBankConnections && (
          <>
            <Box mb={11}>
              <Typography variant="subtitle2" mb={11}>
                {t("settings.advanced.bankImport.title")}
              </Typography>
              <Typography variant="body4" mb={3}>
                {t("settings.advanced.deleteConnection.title")}
              </Typography>
              <Typography variant="body3" color="textSecondary" mb={5}>
                <Trans
                  i18nKey="settings.advanced.deleteConnection.text"
                  components={{
                    linkText: (
                      <Link
                        rel="norefferer"
                        href="https://www.finapi.io/"
                        target="_blank"
                      />
                    ),
                  }}
                />
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                mb={5}
                color="primary.dark"
              >
                <InfoIcon width="20px" height="20px" />
                <Typography variant="body3">
                  {t("settings.advanced.deleteConnection.info")}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setIsConfirmDeleteBankingApiConnectionModalOpen(true)
                }
              >
                {t("settings.advanced.deleteConnection.buttonText")}
              </Button>
            </Box>
            <Divider />
          </>
        )}
        <MandatorConditional hideFor={["kpmg-germany"]}>
          <Box my={11}>
            <Typography variant="subtitle2" mb={3}>
              {t("settings.advanced.tracking.title")}
            </Typography>
            <Typography variant="body3" color="textSecondary" mb={5}>
              {t("settings.advanced.tracking.text")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleTrackingDisabling}
              disabled={isTrackingDisabling}
            >
              {isTrackingEnabled
                ? t("settings.advanced.tracking.buttonTextOff")
                : t("settings.advanced.tracking.buttonTextOn")}
            </Button>
          </Box>
          <Divider />
        </MandatorConditional>
        <Box mt={11}>
          <Typography variant="subtitle2" mb={3}>
            {t("settings.advanced.deleteAccount.title")}
          </Typography>
          <Typography variant="body3" color="textSecondary" mb={5}>
            {t("settings.advanced.deleteAccount.text")}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            mb={5}
            color="error.dark"
          >
            <WarningIcon width="20px" height="20px" />
            <Typography variant="body3">
              {t("settings.advanced.deleteAccount.info")}
            </Typography>
          </Box>
          <Button
            variant="primary"
            color="error"
            size="small"
            onClick={() => setIsConfirmDeleteAccountModalOpen(true)}
          >
            {t("settings.advanced.deleteAccount.buttonText")}
          </Button>
        </Box>
      </Box>
      <ConfirmDeleteDialog
        isOpen={isConfirmDeleteBankingApiConnectionModalOpen}
        title={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.title"
        )}
        text={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.text"
        )}
        cancelButtonText={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.cancel"
        )}
        confirmButtonText={t(
          "settings.advanced.deleteConnection.deleteConnectionConfirmModal.confirm"
        )}
        onClose={() => setIsConfirmDeleteBankingApiConnectionModalOpen(false)}
        onDelete={handleBankingApiConnectionDelete}
        errorMessage={confirmDeleteBankingApiConnectionError}
        isDeleteRequestLoading={isBankingApiConnectionDeleting}
      />
      <ConfirmDeleteDialog
        isOpen={isConfirmDeleteAccountModalOpen}
        title={t(
          "settings.advanced.deleteAccount.deleteAccountConfirmModal.title"
        )}
        text={t(
          "settings.advanced.deleteAccount.deleteAccountConfirmModal.text"
        )}
        cancelButtonText={t(
          "settings.advanced.deleteAccount.deleteAccountConfirmModal.cancel"
        )}
        confirmButtonText={t(
          "settings.advanced.deleteAccount.deleteAccountConfirmModal.confirm"
        )}
        onClose={() => setIsConfirmDeleteAccountModalOpen(false)}
        onDelete={handleUserDelete}
        errorMessage={confirmDeleteAccountError}
        isDeleteRequestLoading={isUserDeleting}
      />
    </SettingsLayout>
  );
};
